<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <FormInput
      name="db-name"
      v-model="fields.dbName"
      testId="db-name-input"
      :prepend="`${site.unixUsername}_`"
      :placeholder="$t('global.db-name')"
      :label="`${$t('global.db-name')} *`"
      :errors="validation.fields.dbName.errors"
      :dirty="validation.fields.dbName.dirty"
      :disabled="isSubmitting"
    />
    <FormInput
      name="username"
      v-model="fields.username"
      testId="username-input"
      :prepend="`${site.unixUsername}_`"
      :placeholder="$t('global.username')"
      :label="`${$t('global.username')} *`"
      :errors="validation.fields.username.errors"
      :dirty="validation.fields.username.dirty"
      :disabled="isSubmitting"
    />
    <div class="text-secondary">{{ $t("global.db-username-requirements") }}</div>
    <div class="d-flex align-items-center mt-4">
      <div class="text-secondary">{{ $t("global.read") }} / {{ $t("global.write") }}</div>
      <div>
        <Toggle v-model="fields.readOnly" :transitioning="isSubmitting" :disabled="isSubmitting" />
      </div>
      <div class="text-secondary">{{ $t("global.read-only") }}</div>
    </div>
    <div class="form-buttons text-right mt-4">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-create-db-user-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="isSubmitting"
        :loading="isSubmitting"
        data-test="create-db-user-button"
      >
        {{ $t("global.add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import store from "@/store";
import Toggle from "@/components/display/Toggle";
import useValidator from "@/utils/useValidator";
import { addDbWithuser as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "DbAddWithUserForm",
  props: {
    site: Object,
  },
  components: {
    Btn,
    FormInput,
    FormMessage,
    Toggle,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({
      dbName: null,
      username: null,
      readOnly: false,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        dbName: {
          required: root.$t("validation.db-name-required"),
        },
        username: {
          required: root.$t("validation.username-required"),
          length: root.$t("validation.db-username-length-required"),
        },
      }
    );

    const cancel = () => {
      emit("cancel");
    };

    const submit = () => {
      errors.value = null;

      validation.validate().then(() => {
        isSubmitting.value = true;
        store
          .dispatch("databases/quickAdd", {
            siteId: props.site.id,
            dbName: fields.dbName,
            username: fields.username,
            readOnly: fields.readOnly,
          })
          .then(() => {
            store.dispatch("toasts/addToast", {
              variant: "success",
              message: root.$t("toasts.db-quick-add-message", {
                dbName: fields.dbName,
                username: fields.username,
              }),
            });
            emit("success");
          })
          .catch((e) => {
            const errors = fromServer(e.request.response);
            if (Array.isArray(errors)) {
              errors.forEach((error) =>
                store.dispatch("toasts/addToast", {
                  variant: "error",
                  message: error,
                  timeout: true,
                })
              );
            } else {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: errors,
                timeout: true,
              });
            }
          })
          .finally(() => {
            isSubmitting.value = false;
          });
      });
    };

    return {
      errors,
      fields,
      isSubmitting,
      submit,
      validation,
      cancel,
    };
  },
};
</script>
<style lang="scss" scoped></style>
